// HU-42 (MG-ILE-61) · tarea 42-2. Administración › Comité de Farmacovigilancia.
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-61.md`. Lo que esta pantalla hace:
//
//   · La COMPOSICIÓN vigente con cargo, nombre, correo y la vigencia que arma el servidor
//     («vigente desde 01/10/2026», C1). Sin país: el Comité es corporativo (RN-40).
//   · Los AVISOS del servidor: cargo del procedimiento sin titular (C5) y las dos advertencias
//     cruzadas con Usuarios y Destinatarios (C6). La pantalla no las calcula.
//   · DESIGNAR, REEMPLAZAR (una sola operación, C2) y RETIRAR (si el Comité quedaría vacío, 422
//     bajo la fecha y nada cambia, C3), cada una en su `Modal`, con las acciones que permite el
//     servidor. Tras cada una se vuelve a pedir el Comité (§8.1 R3).
//   · El HISTORIAL de vigencias (C2): nada se borra (RN-54, RNF-04).
//
// Textos sin criterio que los fije (V-06): las etiquetas de los botones son las de la spec; los
// mensajes de campo, de confirmación, de vacío y de éxito son propuesta y están en el checklist.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { History, RefreshCw, RotateCcw, ShieldOff, UserMinus, UserPlus, UsersRound } from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Input, Modal, PageHeader, Select,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import { designarMiembro, reemplazarMiembro, retirarMiembro, verComite } from '../api/comite';
import type { ComiteDeFarmacovigilancia as DatosDelComite, MiembroDelComite } from '../api/comite';
import { aFallo, MENSAJE_CORREO_INVALIDO } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { esCorreoValido } from '../lib/correo';
import { fechaDeHoyEnZona } from '../lib/fechas';
import { useErroresAlSalir } from '../lib/useErroresAlSalir';
import { puedeDescartar } from '../lib/descarte';

const SIN_ACCESO = 'No tienes acceso a esta opción';

/**
 * El hoy con que nacen las fechas del Comité (RN-82): el Comité no es de ningún caso ni de ningún
 * país, así que su día es el de Lima y no el de la zona del navegador.
 */
const hoyDelComite = (): string => fechaDeHoyEnZona(null);

/** El testid de cada advertencia del servidor, por su `tipo` (spec, «Avisos del servidor»). */
const TESTID_DE_LA_ADVERTENCIA: Record<string, string> = {
  sin_rol_comite: 'comite-advertencia-sin-rol',
  sin_aviso_caso_registrado: 'comite-advertencia-sin-aviso',
};

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'cargo', label: 'Cargo', className: 'w-[28%]', testid: 'comite-tabla-cargo' },
  { key: 'nombre', label: 'Nombre', className: 'w-[22%]', testid: 'comite-tabla-nombre' },
  { key: 'correo', label: 'Correo', className: 'w-[22%]', testid: 'comite-tabla-correo' },
  { key: 'vigencia', label: 'Vigencia', className: 'w-[16%]', testid: 'comite-tabla-vigencia' },
  { key: 'acciones', label: '' },
];

const COLUMNAS_DEL_HISTORIAL: ColumnaDeTabla[] = [
  { key: 'cargo', label: 'Cargo' },
  { key: 'nombre', label: 'Nombre' },
  { key: 'texto_de_la_vigencia', label: 'Vigencia' },
];

type Formulario =
  | { modo: 'designar' }
  | { modo: 'reemplazar'; saliente: MiembroDelComite };

/** El nombre de quien entra: obligatorio y de hasta 200 caracteres. */
function errorDelNombre(nombre: string): string | undefined {
  const texto = nombre.trim();
  if (!texto) return 'Escribe el nombre.';
  if (texto.length > 200) return 'El nombre admite hasta 200 caracteres.';
  return undefined;
}

/**
 * Las reglas del formulario: designar exige el cargo; reemplazar lo hereda del saliente y
 * exige el día desde el que entra. El correo es opcional, pero si se escribe debe ser válido.
 */
function erroresDelMiembro({ esReemplazo, cargo, nombre, correo, fecha }: {
  esReemplazo: boolean; cargo: string; nombre: string; correo: string; fecha: string;
}) {
  return {
    cargo: !esReemplazo && !cargo ? 'Elige el cargo.' : undefined,
    nombre: errorDelNombre(nombre),
    correo: correo.trim() && !esCorreoValido(correo.trim()) ? MENSAJE_CORREO_INVALIDO : undefined,
    fecha: esReemplazo && !fecha ? 'Elige desde qué día entra.' : undefined,
  };
}

function FormularioDeMiembro({ formulario, comite, onCerrar, onGuardado }: {
  formulario: Formulario | null;
  comite: DatosDelComite | null;
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [cargo, setCargo] = useState('');
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [fecha, setFecha] = useState('');
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  const reemplazo = formulario?.modo === 'reemplazar' ? formulario.saliente : null;

  const errores = erroresDelMiembro({ esReemplazo: Boolean(reemplazo), cargo, nombre, correo, fecha });
  const { errorDe, tocar, tocarTodo, reiniciar } = useErroresAlSalir(errores);

  useEffect(() => {
    if (!formulario) return;
    // Designar: el primer cargo sin titular, si hay alguno. Reemplazar: el cargo del saliente.
    setCargo(reemplazo?.cargo ?? comite?.cargos_del_procedimiento_sin_cubrir[0]?.cargo ?? '');
    setNombre('');
    setCorreo('');
    setFecha(reemplazo ? hoyDelComite() : '');
    setFallo(null);
    reiniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir
  }, [formulario]);

  const sucio = Boolean(nombre || correo);

  const guardar = async () => {
    if (!tocarTodo()) return;
    setGuardando(true);
    setFallo(null);
    try {
      const datos = { nombre: nombre.trim(), correo: correo.trim() || null };
      if (reemplazo) {
        await reemplazarMiembro({ saliente_id: reemplazo.miembro_id, cargo: reemplazo.cargo, ...datos, dia: fecha || null });
        onGuardado(`${datos.nombre} reemplaza a ${reemplazo.nombre} en ${reemplazo.cargo}.`);
      } else {
        const designado = await designarMiembro({ cargo, ...datos, desde: fecha || null });
        onGuardado(`${designado.nombre} designado en ${designado.cargo}: ${designado.texto_de_la_vigencia}.`);
      }
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={formulario !== null} onClose={onCerrar} sucio={sucio} data-testid="comite-form"
      eyebrow="Administración · Comité de Farmacovigilancia" icon={reemplazo ? RefreshCw : UserPlus}
      title={reemplazo ? `Reemplazar a ${reemplazo.nombre}` : 'Designar miembro'}
      footer={(
        <>
          <Button variant="secondary" onClick={() => { if (puedeDescartar(sucio)) onCerrar(); }} data-testid="comite-form-cancelar">Cancelar</Button>
          <Button onClick={() => { guardar(); }} disabled={guardando} data-testid="comite-form-guardar">
            {guardando ? 'Guardando…' : (reemplazo ? 'Reemplazar' : 'Designar')}
          </Button>
        </>
      )}>
      <div className="space-y-4">
        {fallo && <Alert tone="danger" title="No se pudo guardar" data-testid="comite-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
        {reemplazo ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-body">
            <div><dt className="text-meta text-ink-soft font-semibold">Sale</dt><dd data-testid="comite-form-saliente">{reemplazo.nombre} · {reemplazo.cargo}</dd></div>
            <div><dt className="text-meta text-ink-soft font-semibold">Cargo</dt><dd data-testid="comite-form-cargo">{reemplazo.cargo}</dd></div>
          </dl>
        ) : (
          <Field label="Cargo" required error={errorDe('cargo')} onTocar={() => tocar('cargo')} campo="cargo">
            <Select data-testid="comite-form-cargo" value={cargo} sinVacio placeholder="Elige el cargo..."
              onChange={(e) => { setCargo(String(e.target.value ?? '')); tocar('cargo'); }}
              options={(comite?.cargos_del_procedimiento ?? []).map((c) => ({ value: c, label: c }))} />
          </Field>
        )}
        <Field label={reemplazo ? 'Nombre de quien entra' : 'Nombre'} required error={errorDe('nombre')} onTocar={() => tocar('nombre')} campo="nombre">
          <Input data-testid="comite-form-nombre" value={nombre} maxLength={200} onChange={(e) => setNombre(e.target.value)} />
        </Field>
        <Field label={reemplazo ? 'Correo de quien entra' : 'Correo'} error={errorDe('correo')} onTocar={() => tocar('correo')} campo="correo"
          hint="Usa el mismo correo que en Usuarios y en Destinatarios de avisos.">
          <Input data-testid="comite-form-correo" type="email" value={correo} maxLength={320} onChange={(e) => setCorreo(e.target.value)} />
        </Field>
        {reemplazo ? (
          <Field label="A partir del" required error={errorDe('fecha')} onTocar={() => tocar('fecha')} campo="fecha"
            hint={`Es el primer día de quien entra; ${reemplazo.nombre} queda vigente hasta el día anterior.`}>
            <Input data-testid="comite-form-dia" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Field>
        ) : (
          <Field label="Vigente desde" hint="Sin fecha, desde hoy.">
            <Input data-testid="comite-form-desde" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Field>
        )}
      </div>
    </Modal>
  );
}

function ConfirmarRetiro({ miembro, onCerrar, onGuardado }: {
  miembro: MiembroDelComite | null;
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [fecha, setFecha] = useState('');
  const [errorDeLaFecha, setErrorDeLaFecha] = useState<string | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!miembro) return;
    setFecha(hoyDelComite());
    setErrorDeLaFecha(null);
    setFallo(null);
  }, [miembro]);

  const retirar = async () => {
    if (!miembro) return;
    if (!fecha) { setErrorDeLaFecha('Elige la fecha de retiro.'); return; }
    setGuardando(true);
    setFallo(null);
    setErrorDeLaFecha(null);
    try {
      const retirado = await retirarMiembro(miembro.miembro_id, fecha);
      onGuardado(`${retirado.nombre} deja el Comité: ${retirado.texto_de_la_vigencia}.`);
    } catch (e) {
      const f = aFallo(e);
      // HU-42-C3: si el Comité quedaría sin miembros vigentes, el mensaje va bajo la fecha.
      if (f.estado === 422) setErrorDeLaFecha(f.message);
      else setFallo(f);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={miembro !== null} onClose={onCerrar} tone="warning" icon={UserMinus} data-testid="comite-modal-retirar"
      eyebrow="Administración · Comité de Farmacovigilancia" title={`¿Retirar a ${miembro?.nombre ?? ''}?`}
      footer={(
        <>
          <Button variant="secondary" onClick={onCerrar} data-testid="comite-form-cancelar">Cancelar</Button>
          {/* Primario y no `danger`: el blanco sobre rose-500 del catálogo no llega a 4,5:1 (lineamiento, par 4); el tono del cuadro ya dice que es una baja. */}
          <Button onClick={() => { retirar(); }} disabled={guardando} data-testid="comite-confirmar-retirar">
            {guardando ? 'Retirando…' : 'Retirar'}
          </Button>
        </>
      )}>
      <div className="space-y-4">
        <p>{miembro?.nombre} deja el cargo {miembro?.cargo} desde esa fecha. Los informes en que participó lo siguen nombrando.</p>
        {fallo && <Alert tone="danger" title="No se pudo retirar" data-testid="comite-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
        <Field label="Fecha de retiro" required error={errorDeLaFecha ?? undefined}>
          <Input data-testid="comite-form-fecha-retiro" type="date" value={fecha}
            onChange={(e) => { setFecha(e.target.value); setErrorDeLaFecha(null); }} />
        </Field>
      </div>
    </Modal>
  );
}

export default function ComiteDeFarmacovigilancia() {
  const [comite, setComite] = useState<DatosDelComite | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [retirarA, setRetirarA] = useState<MiembroDelComite | null>(null);

  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const datos = await verComite();
      if (vivo.current) setComite(datos);
    } catch (e) {
      if (vivo.current) setFallo(aFallo(e));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const hecho = (mensaje: string) => {
    setFormulario(null);
    setRetirarA(null);
    setExito(mensaje);
    cargar();
  };

  const abrir = (accion: () => void) => { setExito(null); accion(); };
  const puedeDesignar = Boolean(comite?.acciones_permitidas.includes('designar'));
  const botonDesignar = puedeDesignar ? (
    <Button icon={UserPlus} onClick={() => abrir(() => setFormulario({ modo: 'designar' }))} data-testid="comite-designar">
      Designar miembro
    </Button>
  ) : undefined;

  const encabezado = (
    <PageHeader
      eyebrow="Administración · Comité de Farmacovigilancia"
      hu="HU-42 · RN-02 / RN-40 / RN-54"
      title="Comité de Farmacovigilancia"
      subtitle="Un solo Comité para toda la organización. Cada designación tiene su vigencia."
      icon={UsersRound}
      actions={!fallo && comite?.miembros.length ? botonDesignar : undefined}
    />
  );

  if (fallo?.estado === 403) {
    return (
      <>
        {encabezado}
        <EmptyState icon={ShieldOff} title={SIN_ACCESO} data-testid="comite-sin-permiso"
          description="El Comité lo configura el administrador del sistema." />
      </>
    );
  }

  const celda = (fila: FilaDeTabla, columna: ColumnaDeTabla): ReactNode => {
    const m = fila as unknown as MiembroDelComite;
    switch (columna.key) {
      case 'cargo': return <span className="font-medium">{m.cargo}</span>;
      case 'nombre': return m.nombre;
      case 'correo': return m.correo ? <span className="break-all">{m.correo}</span> : null;
      case 'vigencia': return <span className="text-meta">{m.texto_de_la_vigencia}</span>;
      case 'acciones':
        return (
          <span className="flex flex-wrap justify-end gap-1.5">
            {m.acciones_permitidas.includes('reemplazar') && (
              <Button size="sm" variant="secondary" icon={RefreshCw} data-testid="comite-tabla-reemplazar"
                aria-label={`Reemplazar a ${m.nombre}`} onClick={() => abrir(() => setFormulario({ modo: 'reemplazar', saliente: m }))}>
                Reemplazar
              </Button>
            )}
            {m.acciones_permitidas.includes('retirar') && (
              <Button size="sm" variant="ghost" icon={UserMinus} data-testid="comite-tabla-retirar"
                aria-label={`Retirar a ${m.nombre}`} onClick={() => abrir(() => setRetirarA(m))}>
                Retirar
              </Button>
            )}
          </span>
        );
      default: return null;
    }
  };

  return (
    <>
      {encabezado}

      {exito && <div className="mb-4"><Alert tone="success" data-testid="comite-exito" onCerrar={() => setExito(null)}>{exito}</Alert></div>}

      {fallo && (
        <div className="space-y-3">
          <Alert tone="danger" title="No se pudo cargar el Comité" data-testid="comite-error" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}{fallo.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} onClick={() => { cargar(); }} data-testid="comite-reintentar">Reintentar</Button>
        </div>
      )}

      {!fallo && !comite && <p data-testid="comite-cargando" role="status" className="text-body text-ink-soft">Cargando el Comité…</p>}

      {!fallo && comite && (
        <div className="space-y-4">
          {(comite.cargos_del_procedimiento_sin_cubrir.length > 0 || comite.advertencias.length > 0) && (
            <div className="space-y-2">
              {comite.cargos_del_procedimiento_sin_cubrir.map((c) => (
                <Alert key={`cargo-${c.cargo}`} tone="warning" data-testid="comite-aviso-cargo-sin-titular">{c.texto}</Alert>
              ))}
              {comite.advertencias.map((a) => (
                <Alert key={`${a.tipo}-${a.texto}`} tone="warning" data-testid={TESTID_DE_LA_ADVERTENCIA[a.tipo]}>{a.texto}</Alert>
              ))}
            </div>
          )}

          <Card title="Composición vigente" icon={UsersRound} pad={false}>
            <DataTable sinMarco columns={COLUMNAS} data-testid="comite-tabla" testidDeFila="comite-tabla-fila"
              rows={comite.miembros.map((m) => ({ ...m, id: m.miembro_id }))} renderCell={celda}
              empty={(
                <EmptyState icon={UsersRound} data-testid="comite-vacio" title="El Comité no tiene miembros vigentes"
                  description="Designa a los integrantes de cada cargo del procedimiento." action={botonDesignar} />
              )} />
          </Card>

          <Card title="Historial de vigencias" icon={History} pad={false}>
            <DataTable sinMarco columns={COLUMNAS_DEL_HISTORIAL} data-testid="comite-historial" testidDeFila="comite-historial-fila" regionDesplazable="Historial de vigencias"
              rows={comite.historial.map((h, i) => ({ ...h, id: `${h.cargo}-${h.nombre}-${h.texto_de_la_vigencia}-${i}` }))}
              empty={<p className="p-5 text-body text-ink-soft">Todavía no hay designaciones.</p>} />
          </Card>
        </div>
      )}

      <FormularioDeMiembro formulario={formulario} comite={comite} onCerrar={() => setFormulario(null)} onGuardado={hecho} />
      <ConfirmarRetiro miembro={retirarA} onCerrar={() => setRetirarA(null)} onGuardado={hecho} />
    </>
  );
}
